import {Link, useParams} from 'react-router-dom';
import {CheckIcon} from '@heroicons/react/24/outline';
import {useGetMeshes, useGetProjects} from 'api/generated';
import {classNames} from 'utils';
import {Info} from 'components';
import {useMe} from 'hooks';

interface IHomeOnboardingProps {}

const HomeOnboarding = ({}: IHomeOnboardingProps) => {
  const params = useParams();
  const workspace = params.workspace ?? '';
  const me = useMe();

  const {data: projectsData} = useGetProjects({filter: 'mine'}, {query: {staleTime: 5 * 1000}});
  const projects = projectsData?.data?.data ?? [];

  const {data: meshesData} = useGetMeshes({filter: 'mine'}, {query: {staleTime: 5 * 1000}});
  const meshes = meshesData?.data?.data ?? [];

  const steps = [
    {
      name: 'Create a project',
      description: (
        <div>
          <p>
            Click{' '}
            <Link to={`/${workspace}/projects`} className="text-primary">
              + New project
            </Link>{' '}
            and fill the form.
          </p>
          <div className="flex items-center gap-1">
            <p>
              If you want to change the handle , you can do it at{' '}
              <Link to={`/${workspace}/settings`} className="text-primary">
                Settings
              </Link>
              .
            </p>
            <Info
              content={
                <p className="text-slate-800 dark:text-white text-sm">
                  The handle is used in project grouping for the url, e.g{' '}
                  <span className="whitespace-nowrap text-primary">{`app.das-ar.com/ar/${me?.handle ?? ''}/{project}`}</span>
                </p>
              }
            />
          </div>
        </div>
      ),
      status: projects.length === 0 ? 'current' : 'complete',
    },
    {
      name: 'Add a model',
      description: (
        <div>
          <p>
            Click{' '}
            <Link to={`/${workspace}/assets`} className="text-primary">
              + New model
            </Link>{' '}
            and add.
          </p>
          <p>
            Recommended models to be &#60;20MB and .glb format.
          </p>
        </div>
      ),
      status: meshes.length === 0 ? 'current' : 'complete',
    },
    {
      name: 'Assign a model and save',
      description: (
        <div>
          <p>
            When inside the project, add models to it and <span className="text-slate-800 dark:text-white font-medium">assign</span> those that you
            want to include in the final AR experience.
          </p>
          <p>
            Then, <span className="text-slate-800 dark:text-white font-medium">save</span> the project.
          </p>
        </div>
      ),
      status: projects[0]?.isSaved ? 'complete' : projects.length > 0 && meshes.length > 0 ? 'current' : 'upcoming',
    },
    {
      name: 'Publish project',
      description: (
        <p>
          After saving, the project is in draft state, click <span className="text-slate-800 dark:text-white font-medium">publish</span> and get the
          AR link or QR code.
        </p>
      ),
      status: projects[0]?.isPublished ? 'complete' : projects[0]?.isSaved ? 'current' : 'upcoming',
    },
    {
      name: 'View AR on mobile',
      description: 'Scan the QR or open the link with your mobile phone or tablet to try it out!',
      status: (meshes.find(m => (m.viewCount ?? 0) > 0)?.viewCount ?? 0) > 0 ? 'complete' : projects[0]?.isPublished ? 'current' : 'upcoming',
    },
  ];

  return (
    <nav aria-label="Progress">
      <ol role="list" className="overflow-hidden">
        {steps.map((step, stepIdx) => (
          <li key={step.name} className={classNames(stepIdx !== steps.length - 1 ? 'pb-10' : '', 'relative')}>
            {step.status === 'complete' ? (
              <>
                {stepIdx !== steps.length - 1 ? (
                  <div aria-hidden="true" className="absolute top-4 left-4 mt-0.5 -ml-px h-full w-0.5 bg-primary" />
                ) : null}
                <div className="group relative flex items-start">
                  <span className="flex h-9 items-center">
                    <span className="relative z-10 flex size-8 items-center justify-center rounded-full bg-primary">
                      <CheckIcon aria-hidden="true" className="size-5 text-white" />
                    </span>
                  </span>
                  <span className="ml-4 flex min-w-0 flex-col gap-1">
                    <span className="text-lg font-medium text-gray-900 dark:text-white">{step.name}</span>
                    <span className="text-sm sm:text-base text-gray-500 dark:text-gray-400">{step.description}</span>
                  </span>
                </div>
              </>
            ) : step.status === 'current' ? (
              <>
                {stepIdx !== steps.length - 1 ? (
                  <div aria-hidden="true" className="absolute top-4 left-4 mt-0.5 -ml-px h-full w-0.5 bg-gray-300 dark:bg-gray-700" />
                ) : null}
                <div className="group relative flex items-start">
                  <span aria-hidden="true" className="flex h-9 items-center">
                    <span className="relative z-10 flex size-8 items-center justify-center rounded-full border-2 border-primary bg-white dark:bg-gray-900">
                      <span className="size-2.5 rounded-full bg-primary" />
                    </span>
                  </span>
                  <span className="ml-4 flex min-w-0 flex-col gap-1">
                    <span className="text-lg font-medium text-primary">{step.name}</span>
                    <span className="text-sm sm:text-base text-gray-500 dark:text-gray-400">{step.description}</span>
                  </span>
                </div>
              </>
            ) : (
              <>
                {stepIdx !== steps.length - 1 ? (
                  <div aria-hidden="true" className="absolute top-4 left-4 mt-0.5 -ml-px h-full w-0.5 bg-gray-300 dark:bg-white/15" />
                ) : null}
                <div className="group relative flex items-start">
                  <span aria-hidden="true" className="flex h-9 items-center">
                    <span className="relative z-10 flex size-8 items-center justify-center rounded-full border-2 border-gray-300 bg-white group-hover:border-gray-400 dark:border-white/15 dark:bg-gray-900 dark:group-hover:border-white/25">
                      <span className="size-2.5 rounded-full bg-transparent group-hover:bg-gray-300 dark:group-hover:bg-white/15" />
                    </span>
                  </span>
                  <span className="ml-4 flex min-w-0 flex-col gap-1">
                    <span className="text-lg font-medium text-gray-500 dark:text-gray-400">{step.name}</span>
                    <span className="text-sm sm:text-base text-gray-500 dark:text-gray-400">{step.description}</span>
                  </span>
                </div>
              </>
            )}
          </li>
        ))}
      </ol>
    </nav>
  );
};

export default HomeOnboarding;
